import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { iqHintAnswer, type SubmissionListItem } from '@jfh/intake-shared';
import { api } from '../lib/api.js';
import { Icon } from '../components/Icon.js';
import { PageHeader, EmptyState } from '../components/Primitives.js';
import { useToast } from '../components/Toast.js';
import { applicationType } from '../inbox/wording.js';
import { ukDayTime } from '../inbox/fieldDisplay.js';
import { problems, type Problem } from './needsFixing.js';

const GROUP_FOR_TYPE: Record<string, string> = {
  trade_account: 'customers',
  credit_account: 'customers',
  supplier_account: 'suppliers',
  lead: 'leads',
};

/**
 * Needs fixing (F10 P5): applications that didn't reach iQ and Teams cards that
 * didn't post, one row per problem, each with its fix.
 */
export function NeedsFixing() {
  const toast = useToast();
  const navigate = useNavigate();
  const [items, setItems] = useState<SubmissionListItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(() => {
    api
      .needsFixing()
      .then((r) => {
        setItems(r.submissions);
        setError(null);
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Couldn’t load the problems'));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const sendAgain = async (p: Problem) => {
    setBusy(p.key);
    try {
      const r = await api.resendIq(p.sub.id);
      if (r.stubbed) toast({ title: 'iQ isn’t connected here', body: 'Nothing was sent.', icon: 'db' });
      else if (r.ok) toast({ title: `In iQ now${r.code ? ` as ${r.code}` : ''}`, icon: 'db' });
      else {
        const answer = iqHintAnswer(r.message);
        toast({
          title: 'iQ still couldn’t take it',
          body: `iQ said: ${r.message ?? 'nothing'}.${answer ? ` Open the application and correct the ${answer}, then send again.` : ''}`,
          icon: 'shield',
        });
      }
      load();
    } catch (e) {
      toast({ title: 'Couldn’t send to iQ', body: (e as Error).message, icon: 'shield' });
    } finally {
      setBusy(null);
    }
  };

  const resendCard = async (p: Problem) => {
    setBusy(p.key);
    try {
      const r = await api.resendCard(p.sub.id);
      toast({ title: `Card sent again to ${r.channel ?? 'Teams'}`, icon: 'teams' });
      load();
    } catch (e) {
      toast({ title: 'The card didn’t send', body: (e as Error).message, icon: 'shield' });
    } finally {
      setBusy(null);
    }
  };

  const rows = items ? problems(items) : null;

  return (
    <div style={{ padding: 26, maxWidth: 1000 }} className="fade-in">
      <PageHeader
        title="Needs fixing"
        subtitle="Applications that didn’t reach iQ, and Teams cards that didn’t post. Fix the cause, then try again."
        actions={
          <button className="btn" onClick={load}>
            <Icon name="search" size={15} /> Check again
          </button>
        }
      />

      {error && (
        <div className="card" style={{ marginTop: 20, padding: 16, color: 'var(--st-declined)' }}>
          {error}
        </div>
      )}

      {!error && rows && rows.length === 0 && (
        <div className="card" style={{ marginTop: 20 }}>
          <EmptyState icon="check" title="Nothing needs fixing" body="Everything is in iQ, and every Teams card posted." />
        </div>
      )}

      {rows && rows.length > 0 && (
        <div className="card" style={{ marginTop: 20, padding: 0, overflow: 'hidden' }}>
          {rows.map((p) => (
            <div
              key={p.key}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 16,
                padding: '16px 20px',
                borderBottom: '1px solid var(--border)',
                flexWrap: 'wrap',
              }}
            >
              <div style={{ minWidth: 260, flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 800, fontSize: 14.5 }}>{p.sub.applicant}</span>
                  <span style={{ fontSize: 12.5, color: 'var(--text-2)' }}>{applicationType(p.sub.type)}</span>
                  <span style={{ fontSize: 12, color: 'var(--text-3)' }}>{ukDayTime(p.at)}</span>
                </div>
                <div style={{ fontSize: 13.5, marginTop: 6 }}>
                  <strong>{p.what}</strong> {p.hint}
                </div>
                <div style={{ fontSize: 12.5, color: 'var(--text-2)', marginTop: 5, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                  {p.said}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, flex: 'none' }}>
                <button
                  className="btn sm"
                  onClick={() => navigate(`/${GROUP_FOR_TYPE[p.sub.type] ?? 'customers'}?open=${p.sub.id}`)}
                >
                  Open application
                </button>
                {p.kind === 'iq' ? (
                  <button className="btn primary sm" disabled={busy === p.key} onClick={() => void sendAgain(p)}>
                    <Icon name="db" size={14} /> {busy === p.key ? 'Sending…' : 'Send again'}
                  </button>
                ) : (
                  <button className="btn primary sm" disabled={busy === p.key} onClick={() => void resendCard(p)}>
                    <Icon name="teams" size={14} /> {busy === p.key ? 'Sending…' : 'Resend card'}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
